// A paid read of the shop that Chat asks the person to approve: a part read again, a new shop or
// one more market, how long it usually takes and what it saves. The parts and times are the
// server's (src/storefront/read-parts.mjs and the read tools' confirmations in risk.mjs); a test
// keeps them equal. Only the tool's name and its fixed `parts` value choose the words.
import type { StorefrontCopyKey } from '@/i18n/ns/storefront';
import type { DiscoveryRun } from './api';

export type StoreRead = 'products' | 'cart' | 'their_chat' | 'all' | 'store' | 'market' | 'checks';
/** The reads with a duration the server confirms; checking saved work again has none. */
export type TimedRead = Exclude<StoreRead, 'checks'>;

const PARTS: StoreRead[] = ['products', 'cart', 'their_chat', 'all'];
const TOOLS: Record<string, (args: Record<string, unknown>) => StoreRead | null> = {
  refresh_storefront_products: (args) => {
    const parts = args.parts ?? 'products';
    return PARTS.find((part) => part === parts) ?? null;
  },
  start_storefront_read: () => 'store',
  add_storefront_market: () => 'market',
  // The saved work's checks again: nothing new is read from the shop.
  check_storefront_read_again: () => 'checks',
};

export const isStoreReadTool = (tool: string) => Object.hasOwn(TOOLS, tool);

/** Every tool approved as a Storefront read card. The canvas and trace lists spread this one list,
 * so a new read tool cannot fall back to the generic request row in one of them. */
export const STORE_READ_TOOLS: readonly string[] = Object.keys(TOOLS);

/** Which read an approval starts: null when it is not a read. */
export const storeRead = (tool: string, args: Record<string, unknown>): StoreRead | null =>
  isStoreReadTool(tool) ? TOOLS[tool](args) : null;

/** The approval's title for a paid read ("Read the products again"), or null for any other tool. */
export function readTitle(
  copy: Readonly<Record<StorefrontCopyKey, string>>,
  tool: string,
  args: Record<string, unknown>,
): string | null {
  const read = storeRead(tool, args);
  return read ? copy[READ_TITLE[read]] : null;
}

/** Usually this many minutes, as the read's confirmation on the server says. */
export const READ_MINUTES: Record<TimedRead, [number, number]> = {
  products: [5, 15],
  cart: [5, 15],
  their_chat: [2, 10],
  all: [10, 30],
  store: [10, 30],
  market: [10, 30],
};

export const READ_TITLE: Record<StoreRead, StorefrontCopyKey> = {
  products: 'readTitleProducts',
  cart: 'readTitleCart',
  their_chat: 'readTitleTheirChat',
  all: 'readTitleAll',
  store: 'readTitleStore',
  market: 'readTitleMarket',
  checks: 'readTitleChecks',
};

export const READ_SCOPE: Record<StoreRead, StorefrontCopyKey> = {
  products: 'readScopeProducts',
  cart: 'readScopeCart',
  their_chat: 'readScopeTheirChat',
  all: 'readScopeAll',
  store: 'readScopeStore',
  market: 'readScopeMarket',
  checks: 'readScopeChecks',
};

/** What a running read covers, from its scope (missing: the whole store). */
export const RUN_READ: Record<string, TimedRead> = {
  product_cards: 'products',
  cart_functions: 'cart',
  support_widget: 'their_chat',
  market: 'market',
  all: 'all',
};

/** The reads that check the cart: a test item goes in and comes out again, never to checkout. */
export const READS_CART: ReadonlySet<StoreRead> = new Set([
  'cart',
  'all',
  'store',
  'market',
  'checks',
]);

type RunScope = NonNullable<DiscoveryRun['scope']>;

/** What the checks of a read of one part are doing, in Setup's step line. A whole-store read (no
 * scope, or all) tries everything. */
export const CHECKS_DETAIL: Partial<Record<RunScope, StorefrontCopyKey>> = {
  product_cards: 'stepDetailChecksProducts',
  cart_functions: 'stepDetailChecksCart',
  support_widget: 'stepDetailChecksChat',
  market: 'stepDetailChecksMarket',
};

/** The same checks as the check card's Checks fact: what they try, and that the shop is not read. */
export const CHECKS_SCOPE: Partial<Record<RunScope, StorefrontCopyKey>> = {
  product_cards: 'readScopeChecksProducts',
  cart_functions: 'readScopeChecksCart',
  support_widget: 'readScopeChecksChat',
  market: 'readScopeChecksMarket',
};

/** Whether a read's checks put a test item in the cart: a whole-store, cart or market read's do. */
export const checksReachCart = (scope?: RunScope | null) =>
  !scope || scope === 'all' || scope === 'cart_functions' || scope === 'market';
